/**
 * Rubato size guide pop-up.
 *
 * Opens from the Open size guide link, converts the centimetre values in the table to
 * inches in the browser at one decimal, and remembers the choice in localStorage.
 * Default is cm, and inches when the market is the US or the UK.
 */

const UNIT_KEY = 'rubato:size-unit';

function readUnit() {
  try {
    return localStorage.getItem(UNIT_KEY);
  } catch {
    return null;
  }
}

function writeUnit(unit) {
  try {
    localStorage.setItem(UNIT_KEY, unit);
  } catch {
    // Private browsing, nothing to do
  }
}

function render(dialog, unit) {
  for (const cell of dialog.querySelectorAll('[data-cm]')) {
    const cm = Number.parseFloat(cell.dataset.cm ?? '');
    if (Number.isNaN(cm)) continue;
    cell.textContent = unit === 'in' ? (cm / 2.54).toFixed(1) : String(cm);
  }

  for (const button of dialog.querySelectorAll('[data-rubato-unit]')) {
    button.setAttribute('aria-pressed', String(button.dataset.rubatoUnit === unit));
  }
}

function setUp(root = document) {
  for (const wrapper of root.querySelectorAll('.rubato-size-guide')) {
    if (wrapper.dataset.rubatoWired === 'true') continue;

    const dialog = wrapper.querySelector('[data-rubato-size-guide-dialog]');
    const open = wrapper.querySelector('[data-rubato-size-guide-open]');
    if (!(dialog instanceof HTMLDialogElement) || !open) continue;

    wrapper.dataset.rubatoWired = 'true';

    const unit = readUnit() ?? dialog.dataset.defaultUnit ?? 'cm';
    render(dialog, unit);

    open.addEventListener('click', () => dialog.showModal());

    wrapper.querySelector('[data-rubato-size-guide-close]')?.addEventListener('click', () => dialog.close());

    // Clicking the scrim closes it; Escape is the dialog's own behaviour
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });

    for (const button of dialog.querySelectorAll('[data-rubato-unit]')) {
      button.addEventListener('click', () => {
        const next = button.dataset.rubatoUnit === 'in' ? 'in' : 'cm';
        writeUnit(next);
        render(dialog, next);
      });
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => setUp(), { once: true });
} else {
  setUp();
}

document.addEventListener('shopify:section:load', () => setUp());

/**
 * A colour sibling swap replaces the product section's markup without a page load, so the
 * size guide trigger and its dialog are new elements with no listeners and no rendered table.
 * rubato-product.js announces the swap and this wires the fresh markup, the same treatment
 * the sticky bar and the Klaviyo trigger already get. The chart itself comes across in the
 * replaced markup, so re-running setUp renders the new product's metaobject data.
 */
document.addEventListener('rubato:product-swapped', () => setUp());

/**
 * The size guides page.
 *
 * setUp above only ever looks at .rubato-size-guide wrappers that hold a <dialog>, so a page
 * of tables with no dialog was skipped entirely and nothing was ever converted. This is the
 * page's own entry point and it touches nothing the pop-up uses: the wiring above is
 * unchanged, and a product page, which has no [data-rubato-size-units], never reaches this.
 *
 * One toggle drives every table on the page, and it writes the same key the pop-up reads, so
 * a customer who chose inches on a product page finds inches here and the other way round.
 */
function setUpPage(root = document) {
  const units = root.querySelector('[data-rubato-size-units]');
  if (!units || units.dataset.rubatoWired === 'true') return;
  units.dataset.rubatoWired = 'true';

  const page = units.closest('.rubato-size-guides') ?? document;
  const unit = readUnit() ?? units.dataset.defaultUnit ?? 'cm';
  render(page, unit);

  for (const button of units.querySelectorAll('[data-rubato-unit]')) {
    button.addEventListener('click', () => {
      const next = button.dataset.rubatoUnit === 'in' ? 'in' : 'cm';
      writeUnit(next);
      render(page, next);
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => setUpPage(), { once: true });
} else {
  setUpPage();
}

document.addEventListener('shopify:section:load', () => setUpPage());
